import React from "react";
import {useKeycloak} from "@react-keycloak/web";
// Project imports
import {CommunicationError, ErrorPage} from "../common/errors";
import {LoadingBackdrop} from "../common/Loading";
import {LibrarySimpleDTO} from "../../models/library";
import ROUTES from "../../routes/routes";
import {API, createApiConfig} from "../../utils";
import {AxiosError} from "axios";
// Material-UI imports
import {Box, Container} from "@material-ui/core";
import {PageHeader, PrimaryPageAction, SecondaryPageAction} from "../common/headers";
import {hasUserRole, ROLES} from "../../utils/auth";
import AddIcon from "@material-ui/icons/Add";
import {LibrariesList} from "./list/LibrariesList";
import {BreadcrumbItem, BreadcrumbsRow} from "../common/breadcrumbs";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import EmptySVG from "../../graphics/undraw/empty_xct9.svg";
import KeyboardIcon from "@material-ui/icons/Keyboard";

const LibrariesEmpty: React.FC = () => {
    const [t] = useTranslation("libraries");
    return (
        <ErrorPage
            title={t("library.empty.public.title")}
            description={t("library.empty.public.description")}
            image={EmptySVG}
        />
    );
};

interface LibrariesPublicItemProps {
    library: LibrarySimpleDTO;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        cardLink: {
            textDecoration: "none",
            display: "block",
            height: "100%",
        },
        libraryCard: {
            backgroundColor: "#ffffff",
            borderRadius: "10px",
            border: "1px solid #E5E7EB",
            padding: theme.spacing(2),
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(0.5),
            height: "100%",
            boxSizing: "border-box" as const,
            position: "relative" as const,
            cursor: "pointer",
            transition: "border-color 0.15s, box-shadow 0.15s",
            "&:hover": {
                borderColor: "#2563EB",
                boxShadow: "0 2px 8px rgba(37,99,235,0.1)",
            },
        },
        libraryVersion: {
            position: "absolute" as const,
            top: theme.spacing(2),
            right: theme.spacing(2),
            fontSize: "0.75rem",
            color: "#6B7280",
            fontWeight: 500,
        },
        libraryTitle: {
            fontSize: "0.9375rem",
            fontWeight: 700,
            color: "#111827",
            paddingRight: "48px",
        },
        libraryOrg: {
            fontSize: "0.8125rem",
            color: "#6B7280",
        },
        libraryMeta: {
            fontSize: "0.8125rem",
            color: "#6B7280",
        },
    })
);

const LibrariesPublicItem: React.FC<LibrariesPublicItemProps> = (props: LibrariesPublicItemProps) => {
    const classes = useStyles();
    return (
        <Link to={ROUTES.library.create(props.library.id)} className={classes.cardLink}>
            <Box className={classes.libraryCard}>
                <span className={classes.libraryVersion}>{props.library.version}</span>
                <Box className={classes.libraryTitle}>{props.library.name}</Box>
                <Box className={classes.libraryOrg}>{props.library.organization.name}</Box>
                {props.library.license && <Box className={classes.libraryMeta}>License: {props.library.license}</Box>}
            </Box>
        </Link>
    );
};

interface LibrariesListState {
    libraries: LibrarySimpleDTO[];
    name: "loading" | "loaded" | "failed";
}

export const LibrariesPublic: React.FC = () => {
    const [t] = useTranslation("libraries");
    const [t2] = useTranslation("values");
    const {keycloak, initialized} = useKeycloak();
    const initState: LibrariesListState = {
        libraries: [],
        name: "loading",
    };
    const [state, setState] = React.useState(initState);
    React.useEffect(() => {
        if (state.name === "loading") {
            API.get<LibrarySimpleDTO[]>(`/libraries/public`, createApiConfig(keycloak, initialized))
                .then((res) => {
                    setState({...state, libraries: res.data, name: "loaded"});
                })
                .catch((err: AxiosError) => {
                    setState({...state, name: "failed"});
                });
        }
    }, [keycloak, initialized, state, setState]);
    if (state.name === "loading") {
        return <LoadingBackdrop />;
    }
    if (state.name === "failed") {
        return <CommunicationError />;
    }
    return (
        <Container>
            <BreadcrumbsRow>
                <BreadcrumbItem name={t("library.titles.libraries")} />
            </BreadcrumbsRow>
            <PageHeader title={t("library.titles.public_libraries")}>
                <SecondaryPageAction
                    title={t2("input_variable.overview.titles.input_variables")}
                    to={ROUTES.inputVariablesOverview.path}
                >
                    <KeyboardIcon />
                </SecondaryPageAction>
                {hasUserRole(keycloak, ROLES.user) && (
                    <PrimaryPageAction title={t("library.titles.new_library")} to={ROUTES.libraryNew.path}>
                        <AddIcon />
                    </PrimaryPageAction>
                )}
            </PageHeader>
            {state.libraries.length > 0 && (
                <LibrariesList libraries={state.libraries}>
                    {(lib) => <LibrariesPublicItem key={lib.id} library={lib} />}
                </LibrariesList>
            )}
            {state.libraries.length === 0 && <LibrariesEmpty />}
        </Container>
    );
};
